import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Cpu, Wifi, WifiOff, HelpCircle, Trash2, Pencil, Users2, DownloadCloud, Zap, KeyRound, Copy, RefreshCw } from "lucide-react";

const empty = { name: "", ip: "", port: 4370, location: "", password: 0 };

const statusPill = (s) => ({
  online: { c: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/10", i: Wifi, t: "En línea" },
  offline: { c: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10", i: WifiOff, t: "Sin conexión" },
  unknown: { c: "text-white/50 border-white/20 bg-white/5", i: HelpCircle, t: "Sin verificar" },
}[s] || { c: "text-white/50 border-white/20 bg-white/5", i: HelpCircle, t: "Sin verificar" });

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState(null);
  const [terminal, setTerminal] = useState([]);
  const [busy, setBusy] = useState("");

  const [agent, setAgent] = useState(null);
  const [agentKey, setAgentKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const load = () => api.get("/devices").then((r) => setDevices(r.data));
  const loadAgent = () => api.get("/agent-status").then((r) => setAgent(r.data)).catch(() => {});
  const loadKey = () => api.get("/my/agent-key").then((r) => setAgentKey(r.data)).catch(() => {});
  useEffect(() => { load(); loadAgent(); loadKey(); const t = setInterval(loadAgent, 30000); return () => clearInterval(t); }, []);

  const copyKey = () => { if (agentKey?.agent_key) { navigator.clipboard.writeText(agentKey.agent_key); toast.success("Clave copiada"); } };
  const regenKey = async () => {
    if (!window.confirm("¿Regenerar la clave del agente? El agente actual dejará de conectarse hasta que la actualices en el asistente.")) return;
    try { const r = await api.post("/my/agent-key/regenerate"); setAgentKey((k) => ({ ...(k || {}), agent_key: r.data.agent_key })); setShowKey(true); toast.success("Clave regenerada"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, port: parseInt(form.port, 10), password: parseInt(form.password || 0, 10) };
      if (form.id) await api.put(`/devices/${form.id}`, payload);
      else await api.post("/devices", payload);
      toast.success("Dispositivo guardado"); setForm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (d) => { if (window.confirm(`¿Eliminar ${d.name}?`)) { await api.delete(`/devices/${d.id}`); load(); } };

  const runAction = async (d, action, labelText) => {
    setBusy(d.id + action);
    setTerminal([`$ ${labelText} :: ${d.name} (${d.ip}:${d.port})`, "$ [ === ] procesando..."]);
    try {
      const { data } = await api.post(`/devices/${d.id}/${action}`);
      setTerminal(data.log || ["$ sin respuesta"]);
      if (data.ok) toast.success(labelText + " completado");
      else toast.error("No se pudo conectar al dispositivo");
      load();
    } catch (err) {
      setTerminal([`$ ERROR: ${formatApiError(err.response?.data?.detail)}`]);
      toast.error("Error de operación");
    } finally { setBusy(""); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><p className="overline text-[#00f0ff]">Hardware ZKTeco</p><h1 className="font-display text-5xl">DISPOSITIVOS</h1></div>
        <button data-testid="device-add-button" onClick={() => setForm({ ...empty })} className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#00c2cc]">
          <Plus className="w-4 h-4" /> Agregar equipo
        </button>
      </div>

      <div className="mb-6 bg-[#00f0ff]/5 border border-[#00f0ff]/30 p-4 text-sm text-white/70">
        <strong className="text-[#00f0ff]">Conexión LAN:</strong> los equipos ZKTeco (ZK Access) deben estar en la misma red local que el servidor para conectar por TCP/IP (protocolo pyzk, puerto 4370). Como alternativa, usa la importación/exportación CSV desde la sección <strong>Socios</strong> para sincronizar con el software ZK Access.
      </div>

      <div data-testid="agent-status-panel" className={`mb-6 flex flex-wrap items-center justify-between gap-3 border p-4 ${agent?.connected ? "border-[#39FF14]/40 bg-[#39FF14]/5" : "border-white/10 bg-[#0a0a0a]"}`}>
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${agent?.connected ? "bg-[#39FF14] animate-pulse" : "bg-white/30"}`} />
          <div>
            <p className="text-sm font-bold">{agent?.connected ? "Agente local CONECTADO" : "Agente local desconectado"}</p>
            <p className="font-mono-logs text-xs text-white/50">
              {agent?.info?.pc_name ? `PC: ${agent.info.pc_name} · ` : ""}
              {agent?.last_seen ? `visto: ${new Date(agent.last_seen).toLocaleString()}` : "sin heartbeat aún"}
            </p>
            {(agent?.info?.branch_name || agent?.info?.device) && (
              <p className="font-mono-logs text-xs text-white/40" data-testid="agent-branch-info">
                {agent?.info?.branch_name ? `Sucursal: ${agent.info.branch_name}` : ""}
                {agent?.info?.device ? ` · Lector: ${agent.info.device}` : ""}
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-white/40 max-w-md">El agente local (carpeta <span className="font-mono-logs">zk_agent</span>) corre en la PC del gimnasio y sincroniza el lector SF300 con la nube en tiempo real.</p>
      </div>

      {agentKey && (
        <div className="mb-6 bg-[#0a0a0a] border border-white/10 p-4" data-testid="agent-key-panel">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-4 h-4 text-[#00f0ff]" />
            <p className="overline">Clave del agente · {agentKey.gym_name || "Sucursal"}</p>
          </div>
          <p className="text-xs text-white/40 mb-3">Pega esta clave en el asistente de instalación del agente (una por sucursal). Mantenla en secreto.</p>
          <div className="flex flex-wrap items-center gap-2">
            <code data-testid="agent-key-value" className="font-mono-logs text-sm bg-[#121212] border border-white/10 px-3 py-2 flex-1 min-w-[240px] break-all">
              {showKey ? agentKey.agent_key : "•".repeat(Math.min(32, (agentKey.agent_key || "").length))}
            </code>
            <button data-testid="agent-key-reveal" onClick={() => setShowKey((s) => !s)} className="border border-white/20 px-3 py-2 text-xs hover:bg-white/5">{showKey ? "Ocultar" : "Mostrar"}</button>
            <button data-testid="agent-key-copy" onClick={copyKey} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white/5"><Copy className="w-3.5 h-3.5" /> Copiar</button>
            <button data-testid="agent-key-regenerate" onClick={regenKey} className="flex items-center gap-1.5 border border-[#FF3B30]/30 text-[#FF3B30] px-3 py-2 text-xs hover:bg-[#FF3B30]/10"><RefreshCw className="w-3.5 h-3.5" /> Regenerar</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {devices.length === 0 && <p className="text-white/30 text-sm">Sin dispositivos configurados.</p>}
        {devices.map((d) => {
          const sp = statusPill(d.status);
          return (
            <div key={d.id} data-testid={`device-card-${d.id}`} className="bg-[#0a0a0a] border border-white/10 p-6 hover:border-white/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-[#00f0ff]/30 flex items-center justify-center"><Cpu className="w-5 h-5 text-[#00f0ff]" /></div>
                  <div><p className="font-bold">{d.name}</p><p className="font-mono-logs text-xs text-white/50">{d.ip}:{d.port}</p></div>
                </div>
                <span className={`flex items-center gap-1.5 px-2 py-1 text-xs font-bold uppercase border ${sp.c}`}><sp.i className="w-3.5 h-3.5" />{sp.t}</span>
              </div>
              <div className="mt-4 text-xs text-white/40 space-y-1 font-mono-logs">
                <p>Ubicación: {d.location || "—"}</p>
                <p>Serie: {d.serial || "—"}</p>
                <p>Última sinc.: {d.last_sync ? new Date(d.last_sync).toLocaleString() : "nunca"}</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button data-testid={`device-test-${d.id}`} disabled={busy} onClick={() => runAction(d, "test", "Probar conexión")} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white/5 disabled:opacity-40"><Zap className="w-3.5 h-3.5" /> Probar</button>
                <button data-testid={`device-sync-${d.id}`} disabled={busy} onClick={() => runAction(d, "sync-users", "Sincronizar socios")} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white/5 disabled:opacity-40"><Users2 className="w-3.5 h-3.5" /> Enviar socios</button>
                <button data-testid={`device-pull-${d.id}`} disabled={busy} onClick={() => runAction(d, "pull-logs", "Descargar marcaciones")} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white/5 disabled:opacity-40"><DownloadCloud className="w-3.5 h-3.5" /> Marcaciones</button>
                <button data-testid={`device-edit-${d.id}`} onClick={() => setForm(d)} className="p-2 hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`device-delete-${d.id}`} onClick={() => del(d)} className="p-2 hover:bg-white/10 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-black border border-[#00f0ff]/30 p-4 scanlines" data-testid="device-terminal">
        <p className="overline text-[#00f0ff] mb-2">Terminal ZK // salida</p>
        <div className="font-mono-logs text-xs text-[#39FF14] space-y-1 min-h-[80px]">
          {terminal.length === 0 ? <p className="text-white/30">$ esperando operación...</p> : terminal.map((l, i) => <p key={`${i}:${l}`} className={l.includes("ERROR") ? "text-[#FF3B30]" : ""}>{l}</p>)}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto">
          <form onSubmit={save} data-testid="device-form" className="bg-[#0a0a0a] border border-white/20 w-full max-w-md my-12">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="font-display text-2xl">{form.id ? "EDITAR EQUIPO" : "NUEVO EQUIPO ZK"}</h3>
              <button type="button" onClick={() => setForm(null)}><X className="w-5 h-5 text-white/50" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="overline block mb-1.5">Nombre *</label>
                <input data-testid="device-field-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="overline block mb-1.5">IP *</label>
                  <input data-testid="device-field-ip" required value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} placeholder="192.168.1.201" className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm font-mono-logs focus:border-[#00f0ff] focus:outline-none" /></div>
                <div><label className="overline block mb-1.5">Puerto</label>
                  <input data-testid="device-field-port" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm font-mono-logs focus:border-[#00f0ff] focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="overline block mb-1.5">Ubicación</label>
                  <input data-testid="device-field-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
                <div><label className="overline block mb-1.5">Clave comm (0)</label>
                  <input data-testid="device-field-password" type="number" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm font-mono-logs focus:border-[#00f0ff] focus:outline-none" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
              <button type="button" onClick={() => setForm(null)} className="border border-white/20 px-5 py-2.5 text-sm hover:bg-white/5">Cancelar</button>
              <button type="submit" data-testid="device-save-button" className="bg-[#00f0ff] text-black font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#00c2cc]">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
